/**
 * The Trost Scroll Sliding & 3D Card Stacking Engine
 * Adapted for Monks Hemp on Shopify (Dawn/abnxvtest)
 * 
 * Replicates the exact smooth slide-up reveal transitions, curved card stacking,
 * and 3D perspective tucking effects of thetrost.com across every page.
 */

(function () {
  let isTicking = false;

  function initTrostScrollAnimations() {
    // Target all sections and candidate modular blocks inside MainContent
    const candidateSelectors = [
      '.animate-section',
      '#MainContent section',
      '#MainContent > .shopify-section',
      '#MainContent > div > .shopify-section'
    ];

    const rawElements = Array.from(document.querySelectorAll(candidateSelectors.join(', ')));
    const animatedElements = [];

    rawElements.forEach((el) => {
      // Exclude header navbar and drawer wrappers from sliding
      if (
        el.closest('.section-header') ||
        el.classList.contains('section-header') ||
        el.classList.contains('shopify-section-group-header-group') ||
        el.classList.contains('t-nav-container') ||
        el.closest('.t-nav-container') ||
        el.classList.contains('trost-cart-drawer') ||
        el.closest('.trost-cart-drawer') ||
        el.tagName === 'HEADER'
      ) {
        return;
      }

      // Avoid double animation: if this container has child <section> elements,
      // let the child sections animate instead of the outer parent wrapper.
      const hasChildSection = el.querySelector('section');
      if (hasChildSection && hasChildSection !== el) {
        return;
      }

      if (!animatedElements.includes(el)) {
        animatedElements.push(el);
      }
    });

    if (!animatedElements.length) return;

    // IntersectionObserver with Trost's exact 5% threshold and rootMargin
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
            obs.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.05,
        rootMargin: '0px 0px -40px 0px'
      }
    );

    const windowHeight = window.innerHeight || document.documentElement.clientHeight;

    animatedElements.forEach((el) => {
      el.classList.add('animate-section');

      const rect = el.getBoundingClientRect();
      // If element is already in the viewport on initial page load, animate immediately
      if (rect.top < windowHeight && rect.bottom > 0) {
        el.classList.add('animate');
      } else {
        observer.observe(el);
      }
    });
  }

  // 3D Card Stacking & Perspective Tucking Engine (Exact Trost effect)
  let stackCards = [];
  let cardStates = [];
  let isStackRunning = false;
  let stackAnimFrame = null;
  const lerp = (start, end, amt) => start + (end - start) * amt;
  const LERP_RATE = 0.08;

  function initStackCards() {
    const stackSelectors = [
      '.t-hero-section',
      '.t-sellers-section',
      '.t-categories-section',
      '.t-how-section',
      '.t-seasonal-section',
      '.t-story-section',
      '.t-clear-section',
      '.t-badges-section',
      '.t-boom-section',
      '.t-pair-section',
      '.t-blogs-section',
      '.t-press-section',
      '.t-reviews-template-section',
      '.t-newsletter-section'
    ];

    // Query cards in DOM order inside MainContent
    const allMatches = Array.from(document.querySelectorAll(stackSelectors.join(', ')));
    stackCards = allMatches.filter((card) => {
      return card.closest('#MainContent') && card.offsetParent !== null;
    });

    // Ensure ascending z-index on parent .shopify-section elements for proper stacking order
    const parentSections = Array.from(document.querySelectorAll('#MainContent > .shopify-section'));
    parentSections.forEach((sec, idx) => {
      sec.style.zIndex = String(idx + 1);
    });

    cardStates = stackCards.map(() => ({
      scale: 1,
      brightness: 1,
      rotX: 0
    }));

    triggerStackUpdate();
  }

  function render3DStack() {
    if (window.innerWidth < 769) {
      stackCards.forEach((card) => {
        card.style.transform = '';
        card.style.filter = '';
      });
      isStackRunning = false;
      return;
    }

    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    let needsNextFrame = false;

    stackCards.forEach((card, i) => {
      const nextCard = stackCards[i + 1];
      const state = cardStates[i];
      if (!state) return;

      let targetScale = 1;
      let targetBrightness = 1;
      let targetRotX = 0;

      if (nextCard) {
        const nextRect = nextCard.getBoundingClientRect();
        // u goes from 0 (when nextCard top is at bottom of viewport) to 1 (when nextCard top reaches 0)
        const u = Math.max(0, Math.min(1, 1 - nextRect.top / windowHeight));
        targetScale = 1 - u * 0.055;
        targetBrightness = 1 - u * 0.28;
        targetRotX = u * 2.5;
      }

      state.scale = lerp(state.scale, targetScale, LERP_RATE);
      state.brightness = lerp(state.brightness, targetBrightness, LERP_RATE);
      state.rotX = lerp(state.rotX, targetRotX, LERP_RATE);

      const diffScale = Math.abs(state.scale - targetScale);
      const diffBrightness = Math.abs(state.brightness - targetBrightness);
      const diffRotX = Math.abs(state.rotX - targetRotX);

      if (diffScale > 0.0003 || diffBrightness > 0.0003 || diffRotX > 0.01) {
        needsNextFrame = true;
      }

      // If at rest and uncompressed, clear inline styles for crisp render
      if (targetScale === 1 && state.scale > 0.999 && state.brightness > 0.999 && Math.abs(state.rotX) < 0.02) {
        if (card.style.transform) card.style.transform = '';
        if (card.style.filter) card.style.filter = '';
      } else {
        card.style.transformOrigin = 'top center';
        card.style.transform = `perspective(1200px) rotateX(${state.rotX.toFixed(3)}deg) scale(${state.scale.toFixed(4)})`;
        card.style.filter = `brightness(${state.brightness.toFixed(4)})`;
      }
    });

    // Hero title & content dynamic scaling on initial scroll
    const heroContent = document.querySelector('.t-hero-content');
    if (heroContent) {
      const scrollY = window.scrollY || window.pageYOffset;
      const heroThreshold = windowHeight * 0.55;
      if (scrollY < heroThreshold) {
        const p = Math.min(scrollY / heroThreshold, 1);
        const scale = 1 - p * 0.16;
        const opacity = 1 - p * 0.55;
        heroContent.style.transform = `scale(${scale.toFixed(3)})`;
        heroContent.style.opacity = `${opacity.toFixed(2)}`;
      } else {
        heroContent.style.opacity = '0.4';
      }
    }

    if (needsNextFrame) {
      stackAnimFrame = requestAnimationFrame(render3DStack);
    } else {
      isStackRunning = false;
    }
  }

  function triggerStackUpdate() {
    if (window.innerWidth < 769 || isStackRunning) return;
    isStackRunning = true;
    stackAnimFrame = requestAnimationFrame(render3DStack);
  }

  window.addEventListener('scroll', triggerStackUpdate, { passive: true });
  window.addEventListener('resize', () => {
    if (window.innerWidth < 769) {
      stackCards.forEach((card) => {
        card.style.transform = '';
        card.style.filter = '';
      });
      isStackRunning = false;
    } else {
      initStackCards();
    }
  });

  // Execute on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initTrostScrollAnimations();
      initStackCards();
    });
  } else {
    initTrostScrollAnimations();
    initStackCards();
  }

  // Re-run on full window load to catch late-hydrated imagery
  window.addEventListener('load', () => {
    initTrostScrollAnimations();
    initStackCards();
  });

  // Support Shopify Theme Editor dynamic section reloads
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => {
        initTrostScrollAnimations();
        initStackCards();
      }, 100);
    });
    document.addEventListener('shopify:section:reorder', () => {
      setTimeout(() => {
        initStackCards();
      }, 100);
    });
  }

  // Global Trost Quick-Add Button Handler (Landing Page & Shop Page)
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.trost-quick-add');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    var originalHTML = btn.innerHTML;
    btn.innerHTML = '<span>...</span>';
    btn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: 1 }] })
    })
    .then(function(res) {
      if (!res.ok) throw new Error('Add to cart failed');
      return res.json();
    })
    .then(function(item) {
      btn.innerHTML = '<span>✓</span>';
      btn.style.background = '#2d5538';
      setTimeout(function() {
        btn.innerHTML = originalHTML;
        btn.style.background = '';
        btn.disabled = false;
      }, 1600);

      // Refresh cart count everywhere
      fetch('/cart.js')
        .then(function(res) { return res.json(); })
        .then(function(cart) {
          document.querySelectorAll('#t-cart-count, .cart-count-bubble, [data-cart-count]').forEach(function(el) {
            el.textContent = cart.item_count;
          });
        });

      // Dawn publish event
      if (window.publish && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'trost-quick-add' });
      }
    })
    .catch(function(err) {
      console.error(err);
      btn.innerHTML = originalHTML;
      btn.disabled = false;
    });
  });

  // Smooth scroll for anchor links
  document.addEventListener('click', function(e) {
    var anchor = e.target.closest('a[href^="#"]');
    if (!anchor) return;
    var hash = anchor.getAttribute('href');
    if (hash && hash.length > 1) {
      var targetEl = document.querySelector(hash);
      if (targetEl) {
        e.preventDefault();
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  });

  // Visual testing helper: ?preview_section=bestsellers
  if (window.location.search.includes('preview_section=bestsellers')) {
    var hero = document.querySelector('.t-hero-section');
    if (hero) hero.style.display = 'none';
    var popups = document.querySelectorAll('.popup-offer-modal, #popup-offer-modal, .popup-offer-overlay, [id*="popup-offer"]');
    popups.forEach(function(p) { p.remove(); });
  }
})();
